/**
 * WCAG 2.1 contrast utilities for runtime brand-color validation.
 * Mirrors tilt-admin's lib/brand-color.ts; kept in sync manually.
 * Input: HSL triples like "221 83% 53%" (the brand_config wire format).
 */

const HSL_TRIPLE_RE = /^\s*(\d{1,3}(?:\.\d+)?)\s+(\d{1,3}(?:\.\d+)?)%\s+(\d{1,3}(?:\.\d+)?)%\s*$/;

function hslTripleToHex(triple: string): string {
  const m = triple.match(HSL_TRIPLE_RE);
  if (!m) return "#000000";
  const h = Number(m[1]) % 360;
  const s = Math.min(100, Math.max(0, Number(m[2]))) / 100;
  const l = Math.min(100, Math.max(0, Number(m[3]))) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0,
    g1 = 0,
    b1 = 0;
  if (hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (hp < 3) [r1, g1, b1] = [0, c, x];
  else if (hp < 4) [r1, g1, b1] = [0, x, c];
  else if (hp < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m0 = l - c / 2;
  const r = Math.round((r1 + m0) * 255);
  const g = Math.round((g1 + m0) * 255);
  const b = Math.round((b1 + m0) * 255);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function srgbToLinear(c: number): number {
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function relativeLuminance(hslTriple: string): number | null {
  const hex = hslTripleToHex(hslTriple);
  const m = /^#?([\da-f]{6})$/i.exec(hex);
  if (!m) return null;
  const iv = parseInt(m[1]!, 16);
  const r = srgbToLinear(((iv >> 16) & 0xff) / 255);
  const g = srgbToLinear(((iv >> 8) & 0xff) / 255);
  const b = srgbToLinear((iv & 0xff) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.1 contrast ratio between two HSL triples. */
export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la == null || lb == null) return null;
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}
